import { ApplicationElement, html } from '@crossiety/lit-components';
import style from './style.lit.css';
import debounce from 'lodash-es/debounce.js';
import translations from './translations.js';

function fetchWithTimeout(url, timeout, options) {
  const controller = new AbortController();

  setTimeout(() => controller.abort(), timeout);
  options.signal = controller.signal;
  return fetch(url, options);
}

/**
 * This component will present offline notice when connection goes offline.
 *
 * It listens to the two properties:
 *   1. navigator.onLine - Which is not enough as it will say online if user just have network connection without internet
 *   2. we periodically try to fetch specified url with HEAD request. Great resource is `favicon.ico`.
 *       HEAD - request is much smaller as it does not download the resource only HTTP headers
 *
 * It only display status online if the user was in offline state so it does not spam user with messages and we also implemented
 * debounce so multiple quick changes are discarded.
 *
 * **NOTE:** This story currently does not support changing of properties via control.
 *
 * To test it you can do following:
 *   - In Chrome - Developer Tools -> Network - Set to offline
 *   - or open story in fullscreen and type into browser  console type: `document.querySelector('xs-network-notice').testState = !document.querySelector('xs-network-notice').testState`
 * @prop theme - (options: /, one)
 */
export class NetworkNotice extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    timeout: { type: Number },
    interval: { type: Number },
    online: { type: Boolean, reflect: true },
    theme: { type: String, reflect: true },
    href: { type: String },
  };

  /** @internal */
  static browserState = navigator.onLine;

  /** @internal */
  static testState = true;

  /** @internal */
  static lastCheckedAt;

  static setOnline() {
    const prevTestState = this.testState;

    this.testState = true;
    this.lastCheckedAt = new Date();

    if (prevTestState !== this.testState)
      window.dispatchEvent(new Event('network-notice-status-changed'));
  }

  constructor() {
    super();
    this.interval = 10000;
    this.timeout = 10000;

    this.path = '/';

    /** @internal */
    this.debouncedUpdate = debounce(() => this.onDebouncedUpdate(), 800);
  }

  connectedCallback() {
    super.connectedCallback();
    this.startChecking();
    document.addEventListener('visibilitychange', this);
    window.addEventListener('online', this);
    window.addEventListener('offline', this);
    window.addEventListener('network-notice-status-changed', this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('visibilitychange', this);
    window.removeEventListener('online', this);
    window.removeEventListener('offline', this);
    window.removeEventListener('network-notice-status-changed', this);
    clearInterval(this.checkingInterval);
  }

  willUpdate() {
    super.willUpdate();
    this.online = this.finalState();
  }

  updated(changedProperties) {
    if (changedProperties.has('online')) {
      this.debouncedUpdate();
    }
  }

  /** @internal */
  get offline() {
    return !this.online;
  }

  /** @internal */
  get isUpToDate() {
    return new Date() - this.constructor.lastCheckedAt < this.interval;
  }

  /** @internal */
  get main() {
    return this.shadowRoot.querySelector('main');
  }

  /** @internal */
  get checkURL() {
    return new URL(this.href, window.location.origin);
  }

  async onDebouncedUpdate() {
    if (this.offline) {
      this.main.style.opacity = 1;
      this.wasOffline = true;
    }

    if (this.online && this.wasOffline) {
      this.main.style.opacity = 0;
    }

    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  handleEvent(e) {
    if (e.type === 'visibilitychange') {
      document.visibilityState === 'visible'
        ? this.startChecking()
        : clearInterval(this.checkingInterval);
    } else {
      this.constructor.browserState = navigator.onLine;
      this.requestUpdate();
    }
  }

  startChecking() {
    if (this.timeout > this.interval) {
      throw new Error(
        `Timeout cannot be larger than interval. Interval: ${this.interval}, Timeout: ${this.timeout}`
      );
    }

    this.checkingInterval = setInterval(() => {
      this.testConnection();
    }, this.interval);
  }

  finalState() {
    return [this.constructor.browserState, this.constructor.testState].every(
      Boolean
    );
  }

  testConnection() {
    if (this.isUpToDate) return;

    this.timingStart = new Date();

    fetchWithTimeout(this.checkURL, this.timeout, {
      cache: 'no-cache',
      method: 'HEAD',
    })
      .then(() => (this.constructor.testState = true))
      .catch(() => (this.constructor.testState = false))
      .finally(() => {
        this.constructor.lastCheckedAt = this.timingEnd = new Date();
        this.timingDiff = this.timingEnd - this.timingStart;
        this.dispatchEvent(
          new Event('fetch:end', { bubbles: true, composed: true })
        );
        this.requestUpdate();
      });
  }

  render() {
    return html`
      <main>${this.online ? this.t('online') : this.t('offline')}</main>
    `;
  }
}

export default NetworkNotice;

NetworkNotice.registerTagName('xs-network-notice');

customElements.get('xs-network-notice') ||
  customElements.define('xs-network-notice', NetworkNotice);
